/**
 * Central place to control which **waves** of listing spec rules are hard-enforced
 * in sell UI, create listing, and item PATCH. Must stay aligned with
 * `ListingSpecWave` in `specRequirements.ts` (`wave_1` | `wave_2` | `wave_3`).
 *
 * **Default** (no env): `wave_1` only.
 *
 * **Optional env** (client + server in Next): comma-separated list, e.g.
 * `NEXT_PUBLIC_LISTING_SPEC_ENABLED_WAVES=wave_1,wave_2`
 * Unknown tokens are ignored; empty after parse falls back to `wave_1`.
 */
type ListingSpecWaveId = "wave_1" | "wave_2" | "wave_3";

const WAVE_SET: Record<string, ListingSpecWaveId> = {
  wave_1: "wave_1",
  wave_2: "wave_2",
  wave_3: "wave_3",
};

const DEFAULT: readonly ListingSpecWaveId[] = ["wave_1"];

function parseEnabledWavesFromEnv(): readonly ListingSpecWaveId[] {
  if (typeof process === "undefined" || !process.env) {
    return DEFAULT;
  }
  const raw = process.env.NEXT_PUBLIC_LISTING_SPEC_ENABLED_WAVES;
  if (typeof raw !== "string" || !raw.trim()) {
    return DEFAULT;
  }
  const out: ListingSpecWaveId[] = [];
  for (const part of raw.split(",")) {
    const t = part.trim();
    if (t in WAVE_SET) {
      out.push(WAVE_SET[t]!);
    }
  }
  return out.length > 0 ? out : DEFAULT;
}

/**
 * Waves that participate in hard enforcement (`shouldEnforce` in
 * `getWaveEnforcementStatus`) when the subcategory is also active in catalog.
 */
export const LISTING_SPEC_ENABLED_WAVES: readonly ListingSpecWaveId[] =
  parseEnabledWavesFromEnv();
